import type { AssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import type {
  AssetMetricsParams,
  CoinMetricsDataResponse,
} from '@src/providers/CoinMetricsApiProvider/coinMetrics.types';
import { CM_MAX_PAGE_SIZE } from '../core';
import { mapWithConcurrency } from './assetLeaderboardBatching';

/**
 * Fetches one `/timeseries/asset-metrics` page for the given params, throwing on failure. Satisfied
 * either by `params => triggerGetAssetMetrics(params).unwrap()` (the RTK Query lazy-query trigger, used
 * by per-panel callers) or by a direct `runCoinMetricsQuery`-based fetcher (used by the shared,
 * cross-panel snapshot endpoint, which fetches from inside its own `queryFn` rather than through a
 * second, redundant RTK Query cache entry per batch).
 */
export type FetchAssetMetricsPage = (params: AssetMetricsParams) => Promise<CoinMetricsDataResponse<AssetMetricPoint>>;

/** Parses a CoinMetrics string value to a finite number, or null when absent/non-finite. */
export function parseMetricValue(raw: string | null | undefined): number | null {
  if (raw == null) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/**
 * Percent change from `prior` to `current` (e.g. 100 -> 110 yields 10). Null when either value is
 * missing or `prior` is zero, since a zero-baseline change is undefined rather than infinite/0.
 */
export function computePercentChange(current: number | null, prior: number | null): number | null {
  if (current == null || prior == null || prior === 0) {
    return null;
  }
  return ((current - prior) / prior) * 100;
}

/** Groups `points` by their `asset` field, preserving each asset's point order. */
export function groupPointsByAsset(points: readonly AssetMetricPoint[]): Map<string, AssetMetricPoint[]> {
  const pointsByAsset = new Map<string, AssetMetricPoint[]>();
  for (const point of points) {
    const existing = pointsByAsset.get(point.asset);
    if (existing) {
      existing.push(point);
    } else {
      pointsByAsset.set(point.asset, [point]);
    }
  }
  return pointsByAsset;
}

export interface AssetMetricsBatchResult {
  batch: string[];
  /** null when the batch's request failed — distinct from a successful response with zero points. */
  points: AssetMetricPoint[] | null;
  truncated: boolean;
}

/**
 * Fetches `/timeseries/asset-metrics` for each id batch with bounded concurrency, always with
 * `paging_from: 'end'` (newest data first) and the shared max page size. A batch whose request throws
 * resolves to `{ points: null }` rather than rejecting the whole cycle, so one failed batch degrades
 * only its own assets — every consumer here (daily snapshot, hourly change) relies on that per-batch
 * isolation to carry over the last good data. Shared by `useAssetLeaderboardData` and
 * `useAssetLeaderboardOneHourChange` so the fetch/paging/error-isolation policy lives in one place.
 *
 * `onBatchSettled` fires once per batch as it resolves (success or failure alike), carrying that
 * batch's ids — used to drive initial-load progress. It fires as batches complete, in whatever order
 * concurrency yields, not in `batches` order.
 */
export async function fetchAssetMetricsInBatches(
  fetchPage: FetchAssetMetricsPage,
  batches: string[][],
  params: { metrics: string; frequency: string; limit_per_asset: number },
  concurrency: number,
  onBatchSettled?: (batch: string[]) => void
): Promise<AssetMetricsBatchResult[]> {
  return mapWithConcurrency(batches, concurrency, async batch => {
    try {
      const response = await fetchPage({
        assets: batch.join(','),
        metrics: params.metrics,
        frequency: params.frequency,
        limit_per_asset: params.limit_per_asset,
        page_size: CM_MAX_PAGE_SIZE,
        paging_from: 'end',
      });
      return { batch, points: response.data, truncated: response.truncated === true };
    } catch {
      return { batch, points: null, truncated: false };
    } finally {
      onBatchSettled?.(batch);
    }
  });
}

/**
 * Merges one fetch cycle's per-batch results into the running per-asset point map: a successful
 * batch overwrites its assets' entries with this cycle's points (even if empty, so an asset that
 * genuinely stops reporting data gets cleared); a failed batch (`points: null`) leaves its assets'
 * existing entries untouched, so a transient failure doesn't erase data that was valid a moment ago.
 * Mutates and returns `pointsByAssetId`. Exported for unit testing.
 */
export function mergeBatchResultsIntoPointsByAssetId(
  pointsByAssetId: Map<string, AssetMetricPoint[]>,
  batchResults: AssetMetricsBatchResult[]
): Map<string, AssetMetricPoint[]> {
  for (const { batch, points } of batchResults) {
    if (points == null) {
      continue;
    }
    const pointsByAsset = groupPointsByAsset(points);
    for (const assetId of batch) {
      pointsByAssetId.set(assetId, pointsByAsset.get(assetId) ?? []);
    }
  }
  return pointsByAssetId;
}
